import React, { useState, useEffect } from 'react';
import { 
  X, 
  Plus, 
  Calendar, 
  Users, 
  DollarSign, 
  Mail, 
  MessageSquare,
  MapPin,
  User,
  Phone,
  Trash2,
  Clock,
  Loader2,
  ChevronDown
} from 'lucide-react';
import { Chat, InventoryScheduleDocument, InventoryDetailsResponse, MyLocationVo2 } from '../../types';
import { useAuthStore } from '../../stores/authStore';
import { apiService } from '../../services/api';
import { TRIP_ID, ALL_TRIP_NAMES, getTripIdByName } from '../../constants/tripIds';
import toast from 'react-hot-toast';

interface UserInfo {
  id: string;
  name: string;
  phoneNumber: string;
  email: string;
  gender: 'male' | 'female';
  pickupLocation: MyLocationVo2 | null;
  isPrimary: boolean;
}

interface AddBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  chat: Chat;
}

export const AddBookingModal: React.FC<AddBookingModalProps> = ({ 
  isOpen, 
  onClose, 
  chat 
}) => {
  const [participants, setParticipants] = useState<UserInfo[]>([]);
  const [bulkInput, setBulkInput] = useState('');
  const [ticketPrice, setTicketPrice] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [amountPaid, setAmountPaid] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [selectedSource, setSelectedSource] = useState('');
  const [notes, setNotes] = useState('');
  const [sendSms, setSendSms] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [showSourceDialog, setShowSourceDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [slots, setSlots] = useState<InventoryScheduleDocument[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<InventoryScheduleDocument | null>(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedTrip, setSelectedTrip] = useState<keyof typeof TRIP_ID | null>(null);
  const [showSlotDropdown, setShowSlotDropdown] = useState(false);
  const [inventoryDetails, setInventoryDetails] = useState<InventoryDetailsResponse | null>(null);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);

  const { user } = useAuthStore();

  // Initialize participants when modal opens
  useEffect(() => {
    if (isOpen && participants.length === 0) {
        setParticipants([{
          id: '1',
          name: '',
          phoneNumber: '',
          email: '',
          gender: 'male',
          pickupLocation: null,
          isPrimary: true
        }]);
    }
  }, [isOpen, participants.length]);

  // Load inventory details for the selected trip
  const loadInventoryDetails = async (tripName: keyof typeof TRIP_ID) => {
    setIsLoadingInventory(true);
    
    try {
      const inventoryId = getTripIdByName(tripName);
      const details = await apiService.getInventoryDetails(inventoryId);
      setInventoryDetails(details);
    } catch (error: any) {
      console.error('Failed to load inventory details:', error);
    } finally {
      setIsLoadingInventory(false);
    }
  };

  // Load slots for the selected trip
  const loadSlots = async (tripName: keyof typeof TRIP_ID) => {
    setIsLoadingSlots(true);
    setSlotsError(null);
    setSelectedSlot(null); // Clear selected slot when loading new slots
    
    try {
      const inventoryId = getTripIdByName(tripName);
      const slotsData = await apiService.getSlotsList(inventoryId);
      setSlots(slotsData);
      
      // Also load inventory details for pickup locations
      await loadInventoryDetails(tripName);
    } catch (error: any) {
      console.error('Failed to load slots:', error);
      setSlotsError(error.message || 'Failed to load slots');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  // Load slots when trip selection changes
  useEffect(() => {
    if (selectedTrip) {
      loadSlots(selectedTrip);
    } else {
      setSlots([]);
      setSelectedSlot(null);
    }
  }, [selectedTrip]);

  // Calculate prices when participants or selected slot changes
  useEffect(() => {
    if (selectedSlot) {
      const basePrice = selectedSlot.price;
      const serviceCharge = 0.05; // 5% service charge
      const totalPrice = Math.round(participants.length * basePrice * (1 + serviceCharge));
      setTicketPrice(totalPrice);
      setFinalPrice(totalPrice);
    } else {
      // Default pricing if no slot selected
      const basePrice = 1000;
      const serviceCharge = 0.05;
      const totalPrice = Math.round(participants.length * basePrice * (1 + serviceCharge));
      setTicketPrice(totalPrice);
      setFinalPrice(totalPrice);
    }
  }, [participants.length, selectedSlot]);

  // Calculate discount and remaining amount when prices change
  useEffect(() => {
    setDiscount(Math.round(ticketPrice - finalPrice));
    setRemainingAmount(Math.round(finalPrice - amountPaid));
  }, [ticketPrice, finalPrice, amountPaid]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showSlotDropdown && !target.closest('.slot-dropdown-container')) {
        setShowSlotDropdown(false);
      }
    };

    if (showSlotDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSlotDropdown]);

  const parseBulkInput = (input: string) => {
    const lines = input.split('\n').filter(line => line.trim());
    const newParticipants: UserInfo[] = [];
    
    lines.forEach((line, index) => {
      const parts = line.split(/[\s,]+/).filter(part => part.trim());
      if (parts.length === 0) return;

      const participant: UserInfo = {
        id: `bulk-${index}`,
        name: '',
        phoneNumber: '',
        email: '',
        gender: 'male',
        pickupLocation: null,
        isPrimary: index === 0
      };

      parts.forEach(part => {
        const cleanPart = part.toLowerCase().trim();
        
        // Gender detection
        if (cleanPart === 'female' || cleanPart === 'f') {
          participant.gender = 'female';
        } else if (cleanPart === 'male' || cleanPart === 'm') {
          participant.gender = 'male';
        }
        // Email detection
        else if (part.includes('@')) {
          participant.email = part;
        }
        // Phone detection (numeric)
        else if (/^\d{10,}$/.test(part)) {
          participant.phoneNumber = part;
        }
        // Name assignment
        else if (!participant.name) {
          participant.name = part;
        }
        // Additional name parts
        else if (participant.name && !participant.email && !participant.phoneNumber) {
          participant.name += ' ' + part;
        }
      });

      if (participant.name) {
        newParticipants.push(participant);
      }
    });

    if (newParticipants.length > 0) {
      setParticipants(prev => [...prev, ...newParticipants]);
      setBulkInput('');
    }
  };

  const addParticipant = () => {
    const newParticipant: UserInfo = {
      id: `manual-${Date.now()}`,
      name: '',
      phoneNumber: '',
      email: '',
      gender: 'male',
      pickupLocation: null,
      isPrimary: participants.length === 0
    };
    setParticipants(prev => [...prev, newParticipant]);
  };

  const removeParticipant = (id: string) => {
    setParticipants(prev => {
      const filtered = prev.filter(p => p.id !== id);
      // Ensure at least one participant remains
      if (filtered.length === 0) {
        return [{
          id: '1',
          name: '',
          phoneNumber: '',
          email: '',
          gender: 'male',
          pickupLocation: null,
          isPrimary: true
        }];
      }
      return filtered;
    });
  };

  const updateParticipant = (id: string, field: keyof UserInfo, value: string | boolean | MyLocationVo2 | null) => {
    setParticipants(prev => prev.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handleSubmit = async () => {
    const errors: Record<string, string> = {};

    if (!selectedTrip) {
      errors.trip = 'Please select a trip';
    }

    if (!selectedSource) {
      setShowSourceDialog(true);
      return;
    }

    if (!selectedSlot) {
      errors.slot = 'Please select a slot for the booking';
    }

    if (participants.some(p => !p.name || !p.phoneNumber)) {
      errors.participants = 'All participants must have a name and phone number';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    setIsSubmitting(true);
    
    try {
      // Call the real API to create booking
      const bookingData = {
        slotId: selectedSlot.id,
        inventoryId: getTripIdByName(selectedTrip),
        participants: participants.map(p => ({
          name: p.name,
          phoneNumber: p.phoneNumber,
          email: p.email,
          gender: p.gender,
          pickupLocation: p.pickupLocation ? {
            id: p.pickupLocation.id,
            name: p.pickupLocation.name,
            displayText: p.pickupLocation.displayText
          } : null
        })),
        ticketPrice,
        finalPrice,
        amountPaid,
        discount,
        source: selectedSource,
        notes,
        sendSms,
        sendEmail
      };

      console.log('Creating booking with data:', bookingData);
      
      const result = await apiService.addBooking(bookingData);
      
      console.log('Booking created successfully:', result);
      toast.success('Booking created successfully!');
      onClose();
    } catch (error: any) {
      console.error('Failed to create booking:', error);
      toast.error(`Failed to create booking: ${error.message || 'Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sources = ['BANBANJARA', 'BMS', 'BOZ', 'MUSKAN', 'ASHRAF'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="booking-dialog-title">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Calendar className="h-6 w-6 text-primary-600" />
            <div>
              <h2 id="booking-dialog-title" className="text-lg font-semibold text-gray-900">Add Booking</h2>
              <p className="text-sm text-gray-500">{chat.name} - {chat.tripName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-4">
          <div className="space-y-6">
            {/* Trip Selection */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Select Trip *
              </label>
              <select
                value={selectedTrip || ''}
                onChange={(e) => setSelectedTrip(e.target.value as keyof typeof TRIP_ID || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Choose a trip...</option>
                {ALL_TRIP_NAMES.map(tripName => (
                  <option key={tripName} value={tripName}>
                    {tripName.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
              {selectedTrip && (
                <div className="mt-2 text-xs text-gray-500">
                  Inventory ID: {getTripIdByName(selectedTrip)}
                </div>
              )}
              {formErrors.trip && (
                <p className="mt-1 text-sm text-red-600" role="alert">{formErrors.trip}</p>
              )}
            </div>

            {/* Slot Selection */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="h-4 w-4 inline mr-1" />
                Select Trip Slot *
              </label>
              
              {!selectedTrip ? (
                <div className="text-gray-500 text-sm py-4 text-center">
                  Please select a trip first to view available slots
                </div>
              ) : isLoadingSlots ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400 mr-2" />
                  <span className="text-gray-500">Loading slots...</span>
                </div>
              ) : slotsError ? (
                <div className="text-red-600 text-sm py-2">
                  {slotsError}
                </div>
              ) : slots.length === 0 ? (
                <div className="text-gray-500 text-sm py-2">
                  No slots available for this trip
                </div>
              ) : (
                <div className="relative slot-dropdown-container">
                  {/* Dropdown Trigger */}
                  <button
                    onClick={() => setShowSlotDropdown(!showSlotDropdown)}
                    className="w-full p-3 text-left rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors flex items-center justify-between"
                  >
                    {selectedSlot ? (
                      <div className="flex items-center justify-between w-full">
                        <div>
                          <div className="font-medium text-gray-900">
                            {selectedSlot.displayTime}
                          </div>
                          <div className="text-sm text-gray-500">
                            {selectedSlot.tripTitle}
                          </div>
                          {selectedSlot.subText && (
                            <div className="text-xs text-gray-400 mt-1">
                              {selectedSlot.subText}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-gray-900">
                            ₹{selectedSlot.price}
                          </div>
                          <div className="text-xs text-gray-500">
                            {selectedSlot.currentBookingCount}/{selectedSlot.maxSlots} booked
                          </div>
                          <div className={`text-xs px-2 py-1 rounded-full mt-1 ${
                            selectedSlot.active 
                              ? selectedSlot.currentBookingCount >= selectedSlot.maxSlots
                                ? 'bg-red-100 text-red-600'
                                : 'bg-green-100 text-green-600'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {selectedSlot.active 
                              ? selectedSlot.currentBookingCount >= selectedSlot.maxSlots 
                                ? 'Full' 
                                : 'Available'
                              : 'Inactive'
                            }
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500">Choose a slot...</span>
                    )}
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${
                      showSlotDropdown ? 'rotate-180' : ''
                    }`} />
                  </button>

                  {/* Dropdown Menu */}
                  {showSlotDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <div className="p-2 space-y-1">
                        {slots.map(slot => (
                          <button
                            key={slot.id}
                            onClick={() => {
                              setSelectedSlot(slot);
                              setShowSlotDropdown(false);
                            }}
                            className={`w-full p-3 text-left rounded-lg border transition-colors ${
                              selectedSlot?.id === slot.id
                                ? 'border-primary-500 bg-primary-50 text-primary-700'
                                : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-gray-900">
                                  {slot.displayTime}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {slot.tripTitle}
                                </div>
                                {slot.subText && (
                                  <div className="text-xs text-gray-400 mt-1">
                                    {slot.subText}
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="font-semibold text-gray-900">
                                  ₹{slot.price}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {slot.currentBookingCount}/{slot.maxSlots} booked
                                </div>
                                <div className={`text-xs px-2 py-1 rounded-full mt-1 ${
                                  slot.active 
                                    ? slot.currentBookingCount >= slot.maxSlots
                                      ? 'bg-red-100 text-red-600'
                                      : 'bg-green-100 text-green-600'
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {slot.active 
                                    ? slot.currentBookingCount >= slot.maxSlots 
                                      ? 'Full' 
                                      : 'Available'
                                    : 'Inactive'
                                  }
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {formErrors.slot && (
                <p className="mt-1 text-sm text-red-600" role="alert">{formErrors.slot}</p>
              )}
            </div>

            {/* Source Selection */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Booking Source
              </label>
              <div className="grid grid-cols-5 gap-2">
                {sources.map(source => (
                  <button
                    key={source}
                    onClick={() => {
                      setSelectedSource(source.toLowerCase());
                      // Auto-enable notifications for specific sources
                      if (['boz', 'muskan', 'ashraf'].includes(source.toLowerCase())) {
                        setSendSms(true);
                        setSendEmail(true);
                      } else {
                        setSendSms(false);
                        setSendEmail(false);
                      }
                    }}
                    className={`p-2 text-sm font-medium rounded-lg transition-colors ${
                      selectedSource === source.toLowerCase()
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {source}
                  </button>
                ))}
              </div>
            </div>

            {/* Bulk Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bulk Add Participants
              </label>
              <textarea
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                placeholder="Enter participant details (one per line):&#10;John Doe 9876543210 john@email.com male&#10;Jane Smith 9876543211 jane@email.com female"
                className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
              <button
                onClick={() => parseBulkInput(bulkInput)}
                disabled={!bulkInput.trim()}
                className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Parse & Add Participants
              </button>
            </div>

            {/* Participants List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Participants ({participants.length})
                </h3>
                {formErrors.participants && (
                  <p className="text-sm text-red-600" role="alert">{formErrors.participants}</p>
                )}
                <button
                  onClick={addParticipant}
                  className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Participant</span>
                </button>
              </div>

              <div className="space-y-4">
                {participants.map((participant, index) => (
                  <div key={participant.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        Person {index + 1} {participant.isPrimary && '(Primary)'}
                      </h4>
                      {participants.length > 1 && (
                        <button
                          onClick={() => removeParticipant(participant.id)}
                          className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                          aria-label={`Remove participant ${index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <User className="h-4 w-4 inline mr-1" />
                          Name *
                        </label>
                        <input
                          type="text"
                          value={participant.name}
                          onChange={(e) => updateParticipant(participant.id, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Enter full name"
                        />
                      </div>

                      {/* Phone */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <Phone className="h-4 w-4 inline mr-1" />
                          Phone *
                        </label>
                        <input
                          type="tel"
                          value={participant.phoneNumber}
                          onChange={(e) => updateParticipant(participant.id, 'phoneNumber', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Enter phone number"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <Mail className="h-4 w-4 inline mr-1" />
                          Email
                        </label>
                        <input
                          type="email"
                          value={participant.email}
                          onChange={(e) => updateParticipant(participant.id, 'email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Enter email address"
                        />
                      </div>

                      {/* Gender */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Gender
                        </label>
                        <select
                          value={participant.gender}
                          onChange={(e) => updateParticipant(participant.id, 'gender', e.target.value as 'male' | 'female')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      </div>

                      {/* Pickup Location */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <MapPin className="h-4 w-4 inline mr-1" />
                          Pickup Location
                        </label>
                        <select
                          value={participant.pickupLocation?.id || ''}
                          onChange={(e) => {
                            const selectedLocation = inventoryDetails?.pickUpLocations?.find(
                              loc => loc.id === e.target.value
                            ) || null;
                            updateParticipant(participant.id, 'pickupLocation', selectedLocation);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          disabled={!inventoryDetails?.pickUpLocations?.length}
                        >
                          <option value="">
                            {!inventoryDetails?.pickUpLocations?.length 
                              ? 'Loading pickup locations...' 
                              : 'Select pickup location'
                            }
                          </option>
                          {inventoryDetails?.pickUpLocations?.map(location => (
                            <option key={location.id} value={location.id}>
                              {location.name}
                            </option>
                          ))}
                        </select>
                        {participant.pickupLocation && (
                          <div className="mt-1 text-xs text-gray-500">
                            {participant.pickupLocation.name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing Section */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Ticket Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <DollarSign className="h-4 w-4 inline mr-1" />
                    Ticket Price (₹)
                  </label>
                  <input
                    type="number"
                    value={ticketPrice}
                    onChange={(e) => setTicketPrice(Math.round(Number(e.target.value)))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    readOnly
                  />
                </div>

                {/* Final Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Final Price (₹) *
                  </label>
                  <input
                    type="number"
                    value={finalPrice}
                    onChange={(e) => setFinalPrice(Math.round(Number(e.target.value)))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                {/* Amount Paid */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount Paid (₹) *
                  </label>
                  <input
                    type="number"
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(Math.round(Number(e.target.value)))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                {/* Discount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount (₹)
                  </label>
                  <input
                    type="number"
                    value={discount}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    readOnly
                  />
                </div>
              </div>

              {/* Remaining Amount */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">Amount Remaining:</span>
                  <span className="text-lg font-semibold text-blue-600">₹{remainingAmount}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full h-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                placeholder="Additional notes or comments..."
              />
            </div>

            {/* Communication Options */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Communication</h3>
              
              <div className="space-y-3">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={sendSms}
                    onChange={(e) => setSendSms(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <MessageSquare className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Send SMS Notification</span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <Mail className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Send Email Notification</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedTrip || !selectedSource || !selectedSlot}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Calendar className="h-4 w-4" />
                <span>Create Booking</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
